
import { Trek } from '@/lib/types/trek-types';
import { Badge } from '@/components/ui/badge';
import { MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbSeparator, BreadcrumbPage } from '@/components/ui/breadcrumb';

interface TrekHeaderProps {
  trek: Trek;
  lowestPrice?: number;
  hasPackages: boolean;
}

const TrekHeader = ({ trek, lowestPrice = 0, hasPackages }: TrekHeaderProps) => {
  // Get difficulty badge color
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'Moderate': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'Difficult': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      case 'Challenging': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800/50 dark:text-gray-300';
    }
  };

  return (
    <div className="mb-6">
      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <Link to="/" className="transition-colors hover:text-nature-600 dark:hover:text-nature-400">
              Home
            </Link>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <Link to="/treks" className="transition-colors hover:text-nature-600 dark:hover:text-nature-400">
              Treks
            </Link>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{trek.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-card/60 backdrop-blur-sm border border-border/40 rounded-xl p-5 shadow-sm">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-gradient-nature">{trek.name}</h1>
          <div className="flex items-center mt-2 text-muted-foreground">
            <MapPin className="h-4 w-4 mr-1 text-nature-500" />
            <span>{trek.location}</span>
          </div>
        </div>
        
        <div className="flex flex-col items-end">
          {hasPackages && lowestPrice > 0 && (
            <>
              <div className="text-sm text-muted-foreground">Starting from</div>
              <div className="text-2xl font-bold text-nature-600 dark:text-nature-400">₹{lowestPrice.toLocaleString()}</div>
            </>
          )}
          <Badge className={`mt-2 ${getDifficultyColor(trek.difficulty)}`}>
            {trek.difficulty}
          </Badge>
        </div>
      </div>
    </div>
  );
};

export default TrekHeader;
